import { Alert, Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Stage = {
  id: string; status: string; notes?: string | null; template_code: string; template_name: string;
  admission_no?: string; first_name?: string; last_name?: string;
};
type Evidence = {
  id: string; title: string; evidence_url?: string | null; note_text?: string | null;
  linked_entity_type?: string | null; linked_entity_id?: string | null; created_at?: string;
};
type Detail = { stage: Stage; evidence: Evidence[] };
type StageValues = { status: string; notes?: string };
type EvidenceValues = {
  title: string; evidence_url?: string; note_text?: string;
  linked_entity_type?: string; linked_entity_id?: string;
};

export default function HpcStageDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stageForm] = Form.useForm<StageValues>();
  const [evidenceForm] = Form.useForm<EvidenceValues>();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.hpc.manage') || permissions.includes('education.admin');
  const detail = useQuery({
    queryKey: ['education-hpc-stage', id],
    queryFn: async () => (await api.get<Detail>(`/education/hpc/stages/${id}`)).data,
  });
  useEffect(() => {
    if (detail.data?.stage) stageForm.setFieldsValue({
      status: detail.data.stage.status,
      notes: detail.data.stage.notes || undefined,
    });
  }, [detail.data?.stage, stageForm]);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-hpc-stage', id] });
    queryClient.invalidateQueries({ queryKey: ['education-hpc-stages'] });
  };
  const updateStage = useMutation({
    mutationFn: (values: StageValues) => api.put(`/education/hpc/stages/${id}`, {
      status: values.status,
      notes: values.notes?.trim() || null,
    }),
    onSuccess: () => { message.success('Student stage updated'); refresh(); },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || 'Unable to update student stage'),
  });
  const addEvidence = useMutation({
    mutationFn: (values: EvidenceValues) => api.post(`/education/hpc/stages/${id}/evidence`, {
      ...values,
      evidence_url: values.evidence_url?.trim() || null,
      note_text: values.note_text?.trim() || null,
      linked_entity_type: values.linked_entity_type || null,
      linked_entity_id: values.linked_entity_id?.trim() || null,
    }),
    onSuccess: () => {
      message.success('Evidence link added');
      evidenceForm.resetFields();
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || 'Unable to add evidence'),
  });
  const removeEvidence = useMutation({
    mutationFn: (evidenceId: string) => api.delete(`/education/hpc/evidence/${evidenceId}`),
    onSuccess: () => { message.success('Evidence link removed'); refresh(); },
  });
  const stage = detail.data?.stage;

  return (
    <EducationPageChrome
      title={stage ? `${stage.template_code} — ${stage.template_name}` : 'HPC student stage'}
      description={stage ? `${stage.admission_no || ''} · ${[stage.first_name, stage.last_name].filter(Boolean).join(' ')}` : undefined}
      extra={<Button onClick={() => navigate('/education/hpc')}>Back to HPC</Button>}
    >
      {!canManage ? <Alert type="info" showIcon message="Read-only access" /> : null}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title="Stage progress" loading={detail.isLoading}>
          <Form form={stageForm} layout="vertical" disabled={!canManage} onFinish={(values) => updateStage.mutate(values)}>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select style={{ width: 200 }} options={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'completed', label: 'Completed' },
              ]} />
            </Form.Item>
            <Form.Item name="notes" label="Stage notes">
              <Input.TextArea rows={4} maxLength={4000} showCount />
            </Form.Item>
            {canManage ? <Button type="primary" htmlType="submit" loading={updateStage.isPending}>Save progress</Button> : null}
          </Form>
        </Card>
        {canManage ? (
          <Card size="small" title="Add evidence link or note">
            <Alert
              type="info" showIcon style={{ marginBottom: 16 }}
              message="Evidence is stored as an http(s) link and/or text note. Files are not uploaded to a separate portfolio vault."
            />
            <Form form={evidenceForm} layout="vertical" onFinish={(values) => addEvidence.mutate(values)}>
              <Form.Item name="title" label="Title" rules={[{ required: true, max: 250 }]}>
                <Input maxLength={250} />
              </Form.Item>
              <Form.Item name="evidence_url" label="Evidence URL" rules={[{ type: 'url', message: 'Enter a valid URL' }]}>
                <Input maxLength={1000} placeholder="https://…" />
              </Form.Item>
              <Form.Item name="note_text" label="Evidence note">
                <Input.TextArea rows={4} maxLength={4000} showCount />
              </Form.Item>
              <Space align="start" wrap>
                <Form.Item name="linked_entity_type" label="Optional source type">
                  <Select allowClear style={{ width: 180 }} options={[
                    { value: 'assessment', label: 'Assessment entry' },
                    { value: 'activity', label: 'Activity achievement' },
                  ]} />
                </Form.Item>
                <Form.Item name="linked_entity_id" label="Optional source ID">
                  <Input style={{ width: 300 }} maxLength={36} />
                </Form.Item>
              </Space>
              <Button type="primary" htmlType="submit" loading={addEvidence.isPending}>Add evidence</Button>
            </Form>
          </Card>
        ) : null}
        <Card size="small" title="Evidence" loading={detail.isLoading}>
          <Table
            rowKey="id" size="small" pagination={false} dataSource={detail.data?.evidence || []}
            columns={[
              { title: 'Title', dataIndex: 'title' },
              {
                title: 'Evidence',
                render: (_: unknown, row: Evidence) => <Space direction="vertical" size={0}>
                  {row.evidence_url ? <a href={row.evidence_url} target="_blank" rel="noreferrer">Open link</a> : null}
                  {row.note_text ? <Typography.Text>{row.note_text}</Typography.Text> : null}
                </Space>,
              },
              {
                title: 'Linked source', width: 180,
                render: (_: unknown, row: Evidence) => row.linked_entity_type
                  ? <Tag>{row.linked_entity_type}: {row.linked_entity_id}</Tag> : '—',
              },
              {
                title: 'Actions', width: 90,
                render: (_: unknown, row: Evidence) => canManage ? (
                  <Popconfirm title="Remove this evidence link?" onConfirm={() => removeEvidence.mutate(row.id)}>
                    <Button size="small" danger>Remove</Button>
                  </Popconfirm>
                ) : null,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
